import React from 'react';
import { Link } from 'react-router-dom';
import Footer from '../components/Footer';
import Reveal from '../components/Reveal';
import './AboutUs.css';

const values = [
  { icon: '🎓', title: 'Excellence',    desc: 'We maintain the highest standards in course quality and instructional delivery.' },
  { icon: '🌍', title: 'Accessibility', desc: 'Education should be accessible to everyone, regardless of geographic or financial constraints.' },
  { icon: '💡', title: 'Innovation',    desc: 'We continuously innovate to provide the best learning technologies and methodologies.' },
  { icon: '🤝', title: 'Community',     desc: 'We foster a supportive community where learners can collaborate and grow together.' },
];

const offerings = [
  '📚 Diverse course catalog across multiple disciplines',
  '👨‍🏫 Expert instructors with industry experience',
  '📱 Mobile-friendly learning experience',
  '🎯 Personalized learning paths',
  '📊 Progress tracking and analytics',
  '🏆 Certification programs',
  '💬 Community support and discussions',
  '⚡ Interactive quizzes and assessments',
];

const missionPoints = [
  'Practice Tests to help students assess their preparation and improve performance',
  'Pre-recorded Lectures for easy and flexible learning anytime, anywhere',
  'Well-structured Notes aligned with the MP Board syllabus',
  'Live Classes for MBA Students, offering interactive sessions and personalized guidance',
  'Notification Section to keep students updated with important announcements and study schedules',
  'Multi-language Support, allowing students to learn in their preferred language',
];

const stats = [
  { num: '10K+', label: 'Active Students' },
  { num: '500+', label: 'Courses' },
  { num: '95%',  label: 'Success Rate' },
  { num: '24/7', label: 'Support' },
];

function AboutUs() {
  return (
    <div>
      {/* ── HERO ── */}
      <section className="about-hero">
        <Reveal>
          <span className="about-hero-badge">🎓 About Ab Meri Baari</span>
        </Reveal>
        <Reveal delay={80}>
          <h1>Empowering Every Student to<br /><span>Reach Their Full Potential</span></h1>
        </Reveal>
        <Reveal delay={160}>
          <p>
            A dedicated digital learning platform built for Class 10 MP Board students and MBA aspirants —
            making quality education simple, accessible, and effective for all.
          </p>
        </Reveal>
        <Reveal delay={240}>
          <Link to="/contact-us" className="btn btn-white btn-lg">Get in Touch</Link>
        </Reveal>
        {/* Wave divider */}
        <div className="about-hero-wave">
          <svg viewBox="0 0 1440 60" preserveAspectRatio="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M0,30 C360,60 1080,0 1440,30 L1440,60 L0,60 Z" fill="#ffffff" />
          </svg>
        </div>
      </section>

      {/* ── STATS STRIP ── */}
      <div className="about-stats">
        <div className="about-stats-inner">
          {stats.map((s, i) => (
            <Reveal key={s.label} delay={i * 80} className="about-stat-item">
              <div className="about-stat-num">{s.num}</div>
              <div className="about-stat-label">{s.label}</div>
            </Reveal>
          ))}
        </div>
      </div>

      {/* ── MISSION ── */}
      <section className="about-mission">
        <div className="about-mission-inner">
          <Reveal direction="right" className="about-mission-img">
            <img
              src="https://images.unsplash.com/photo-1509062522246-3755977927d7?w=700&q=80"
              alt="Students learning"
            />
          </Reveal>
          <Reveal direction="left" className="about-mission-text">
            <span className="section-label">Our Mission</span>
            <h2>Making Quality Education Accessible to All</h2>
            <p>
              Ab Meri Baari is a dedicated digital learning platform designed specifically for Class 10 MP Board students.
              Our mission is to make quality education accessible, simple, and effective for every student, no matter where they are.
            </p>
            <p>We provide a complete learning ecosystem that includes:</p>
            <ul className="about-mission-list">
              {missionPoints.map((p) => <li key={p}>{p}</li>)}
            </ul>
            <p>
              At Ab Meri Baari, we believe every student deserves the right guidance and opportunity to succeed.
              Our goal is to empower students to take charge of their learning journey and confidently say, "Ab Meri Baari!"
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── VALUES ── */}
      <section className="about-values">
        <div className="about-values-inner">
          <Reveal className="about-values-header">
            <span className="section-label">What Drives Us</span>
            <h2 className="section-title">Our Core Values</h2>
            <p className="section-desc">
              These four principles guide every decision we make — from building our platform to supporting our students.
            </p>
          </Reveal>
          <div className="about-values-grid">
            {values.map((v, i) => (
              <Reveal key={v.title} delay={i * 90} className="value-card">
                <span className="value-card-icon">{v.icon}</span>
                <h4>{v.title}</h4>
                <p>{v.desc}</p>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── OFFERINGS ── */}
      <section className="about-offerings">
        <div className="about-offerings-inner">
          <Reveal className="about-offerings-header">
            <span className="section-label">Platform Features</span>
            <h2 className="section-title">Everything We Offer</h2>
            <p className="section-desc">A complete learning ecosystem built for real outcomes.</p>
          </Reveal>
          <div className="about-offerings-grid">
            {offerings.map((o, i) => (
              <Reveal key={o} delay={i * 50} className="offering-item">
                {o}
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── WHY CHOOSE + GET STARTED ── */}
      <section className="about-why">
        <div className="about-why-inner">
          <Reveal className="about-why-block">
            <h2 className="about-why-title">Why Choose Ab Meri Baari?</h2>
            <p>
              With thousands of satisfied learners worldwide, we've established ourselves as a trusted platform for online education.
              Our commitment to quality, innovation, and student success sets us apart. Whether you're looking to start a new career,
              advance your skills, or explore a passion, Ab Meri Baari is your ideal learning partner.
            </p>
          </Reveal>
          <Reveal delay={100} className="about-why-block">
            <h2 className="about-why-title">Get Started Today</h2>
            <p>
              Join our growing community of learners and take the next step in your educational journey.
              Explore our courses and discover the knowledge that awaits you.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="about-cta">
        <Reveal>
          <h2>Ready to Begin Your Journey?</h2>
          <p>Join thousands of students already achieving success with Ab Meri Baari.</p>
          <Link to="/" className="btn btn-white btn-lg">Explore Platform</Link>
        </Reveal>
      </section>

      <Footer />
    </div>
  );
}

export default AboutUs;
